import { useEffect, useState } from "react";
import { useFrameActive } from "../contexts/TabsContext";

// Is a modal popup (Dialog / Sheet / AlertDialog) currently open AND visible?
// The app header reads this to slim down to just the tab strip while an edit /
// quick-action / info popup is up; during normal browsing the full control
// cluster (AI, notifications, approvals, calendar, FY selector, profile) stays.
//
// GROUND TRUTH from the DOM — no manual +1/-1 counter (those desync with
// keep-alive tab frames / StrictMode and get stuck, wrongly hiding the cluster
// on a normal page). Radix tags open modal content with role="dialog" /
// "alertdialog" and data-state="open"; a dialog left mounted in a hidden
// keep-alive tab lives in a display:none frame and has no layout box, so it is
// correctly ignored.

const listeners = new Set();

function anyPanelVisible() {
  if (typeof document === "undefined") return false;
  const nodes = document.querySelectorAll(
    '[role="dialog"][data-state="open"],[role="alertdialog"][data-state="open"]'
  );
  for (const el of nodes) {
    // A visible element (even position:fixed) has ≥1 client rect; a node inside
    // a display:none frame — or mid closing-animation — has none.
    if (el.getClientRects().length > 0) return true;
  }
  return false;
}

let rafId = 0;
const scheduleNotify = () => {
  if (typeof requestAnimationFrame === "undefined") {
    const open = anyPanelVisible();
    listeners.forEach((l) => l(open));
    return;
  }
  cancelAnimationFrame(rafId);
  // Defer one frame so Radix has finished inserting/removing the node and the
  // tab frame has finished toggling display before we measure.
  rafId = requestAnimationFrame(() => {
    const open = anyPanelVisible();
    listeners.forEach((l) => l(open));
  });
};

// Shared observer: fires the moment ANY dialog/sheet/alert opens, closes, or
// flips data-state — independent of React render timing, so detection can't be
// missed.
let observer = null;
const ensureObserver = () => {
  if (observer || typeof MutationObserver === "undefined" || typeof document === "undefined") return;
  observer = new MutationObserver(scheduleNotify);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-state"],
  });
};

// Called by modal content components on mount. The MutationObserver already
// catches open/close; this adds the one thing it can't see — a tab switch,
// which hides the frame via a display change (not a data-state change) and so
// must trigger a re-measure of what's actually visible.
export function useRegisterOpenPanel() {
  const frameActive = useFrameActive();
  useEffect(() => {
    scheduleNotify();
    return scheduleNotify;
  }, [frameActive]);
}

// True while any visible modal panel is open.
export function useAnyPanelOpen() {
  const [open, setOpen] = useState(anyPanelVisible);
  useEffect(() => {
    ensureObserver();
    const l = (v) => setOpen(v);
    listeners.add(l);
    scheduleNotify(); // sync once on mount
    return () => listeners.delete(l);
  }, []);
  return open;
}
